import type { OnboardingState, OperatorClass, Person, Role } from '@bk/contracts';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { humanize } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, ErrorText, Field, Select } from '../../ui';
import { PeopleRolePicker } from './PeopleRolePicker';
import { useSites } from './SiteScope';

export type PersonSection = 'roles' | 'sites' | 'account' | 'details' | 'procedures';
type Dirty = { onDirtyChange: (dirty: boolean) => void };

export function PersonPanel({
  person,
  roles,
  section,
  onSectionChange,
  onDirtyChange,
  hasUnsavedChanges,
  onSaved,
  onChanged,
  onComplete,
}: {
  person: Person;
  roles: Role[];
  section: PersonSection;
  onSectionChange: (section: PersonSection) => void;
  onDirtyChange: (dirty: boolean) => void;
  hasUnsavedChanges: boolean;
  onSaved: (message: string, next?: PersonSection) => void;
  onChanged: () => void;
  onComplete: () => void;
}) {
  const { t, isManager, tenant } = useTenant();
  const sites = useSites();
  const toggle = useMutation({
    mutationFn: () =>
      api(t(`/people/${person.id}`), { method: 'PATCH', body: { active: !person.active } }),
    onSuccess: () => onSaved(person.active ? 'Persona disattivata.' : 'Persona riattivata.'),
  });
  const tabs: Array<[PersonSection, string]> = [
    ['roles', '1. Ruoli'],
    ...(sites.sites.length ? [['sites', '2. Sedi'] as [PersonSection, string]] : []),
    ['account', `${sites.sites.length ? '3' : '2'}. Account`],
    ['details', 'Dati e altre opzioni'],
  ];
  if (!isManager)
    return (
      <p className="text-sm text-ink/65">
        La configurazione delle persone è riservata ai responsabili.
      </p>
    );
  return (
    <>
      <nav
        aria-label={`Configurazione di ${person.name}`}
        className="mb-5 flex flex-wrap gap-1 border-b border-black/[0.08] pb-3"
      >
        {tabs.map(([key, label]) => (
          <Button
            key={key}
            variant="ghost"
            aria-current={
              section === key || (key === 'details' && section === 'procedures')
                ? 'step'
                : undefined
            }
            className={`px-3 py-2 ${section === key || (key === 'details' && section === 'procedures') ? 'bg-accent/10 text-accent' : ''}`}
            onClick={() => onSectionChange(key)}
          >
            {label}
          </Button>
        ))}
      </nav>
      {section === 'roles' && (
        <PersonRolesEditor
          key={`roles:${person.id}`}
          person={person}
          roles={roles}
          onDirtyChange={onDirtyChange}
          onDone={() =>
            onSaved(
              'Ruoli salvati. Ora verifica dove vengono svolti.',
              sites.sites.length ? 'sites' : 'account',
            )
          }
        />
      )}
      {section === 'sites' && (
        <PersonSitesEditor
          key={`sites:${person.id}`}
          person={person}
          roles={roles}
          onDirtyChange={onDirtyChange}
          onDone={() => onSaved('Ambito delle sedi salvato. Ora configura l’accesso.', 'account')}
        />
      )}
      {section === 'account' && (
        <>
          <PersonAccountEditor
            key={`account:${person.id}:${person.user_id ?? ''}`}
            person={person}
            onDone={() => onSaved('Accesso della persona aggiornato.', 'account')}
            onChanged={onChanged}
            onComplete={onComplete}
            onDirtyChange={onDirtyChange}
          />
          <div className="mt-4 border-t border-black/[0.08] pt-4">
            <p className="text-sm text-ink/65">
              Dopo il collegamento, scegli in Accessi ai dati cosa il ruolo può vedere e modificare.
            </p>
            <a
              href={`#/t/${tenant.id}/accessi-dati`}
              className="mt-2 inline-block text-sm font-medium text-accent"
            >
              Configura accessi ai dati →
            </a>
          </div>
        </>
      )}
      {section === 'details' && (
        <>
          <PersonDetailsEditor
            key={`details:${person.id}`}
            person={person}
            onDirtyChange={onDirtyChange}
            onDone={() => onSaved('Dati della persona salvati.')}
          />
          <div className="mt-5 rounded-lg border border-black/10 p-3">
            <p className="text-sm text-ink/65">Gestione delle procedure di un’area</p>
            <Button className="mt-1" variant="ghost" onClick={() => onSectionChange('procedures')}>
              Configura responsabilità sulle procedure →
            </Button>
          </div>
          <div className="mt-5 border-t border-black/[0.08] pt-4">
            <h3 className="text-sm font-medium">Stato della persona</h3>
            <p className="mb-2 mt-1 text-sm text-ink/60">
              {person.active
                ? 'La persona è attiva e può ricevere lavoro secondo i suoi ruoli.'
                : 'La persona è disattivata.'}
            </p>
            {hasUnsavedChanges && (
              <p className="mb-2 text-xs text-ink/60">
                Salva le modifiche ai dati prima di cambiare lo stato della persona.
              </p>
            )}
            <Button
              variant="ghost"
              disabled={toggle.isPending || hasUnsavedChanges}
              onClick={() => toggle.mutate()}
            >
              {person.active ? 'Disattiva persona' : 'Riattiva persona'}
            </Button>
            <ErrorText>{toggle.error ? errorMessage(toggle.error) : ''}</ErrorText>
          </div>
        </>
      )}
      {section === 'procedures' && (
        <>
          <Button className="mb-3" variant="ghost" onClick={() => onSectionChange('details')}>
            ← Dati della persona
          </Button>
          <p className="mb-3 text-sm text-ink/65">
            Questa responsabilità consente di gestire le procedure di un’area. I compiti vengono
            invece assegnati attraverso i ruoli.
          </p>
          <ProcedureAreasEditor
            person={person}
            onDirtyChange={onDirtyChange}
            onDone={() => onSaved('Responsabilità sulle procedure aggiornate.', 'details')}
            onCancel={() => onSectionChange('details')}
          />
        </>
      )}
    </>
  );
}

function PersonRolesEditor({
  person,
  roles,
  onDone,
  onDirtyChange,
}: { person: Person; roles: Role[]; onDone: () => void } & Dirty) {
  const { t } = useTenant();
  const [chosen, setChosen] = useState(person.roles);
  useEffect(
    () =>
      onDirtyChange(
        JSON.stringify([...chosen].sort()) !== JSON.stringify([...person.roles].sort()),
      ),
    [chosen, person.roles, onDirtyChange],
  );
  const save = useMutation({
    mutationFn: () => api(t(`/people/${person.id}`), { method: 'PATCH', body: { roles: chosen } }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-ink/65">
        Quali attività svolge {person.name}? Puoi assegnare più ruoli alla stessa persona e più
        persone allo stesso ruolo.
      </p>
      <PeopleRolePicker roles={roles} value={chosen} onChange={setChosen} />
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <Button data-person-save type="submit" disabled={save.isPending}>
        Salva ruoli e continua
      </Button>
    </form>
  );
}

function PersonDetailsEditor({
  person,
  onDone,
  onDirtyChange,
}: { person: Person; onDone: () => void } & Dirty) {
  const { t } = useTenant();
  const [name, setName] = useState(person.name);
  const [email, setEmail] = useState(person.email ?? '');
  const [phone, setPhone] = useState(person.phone ?? '');
  const [klass, setKlass] = useState(person.operator_class);
  useEffect(
    () =>
      onDirtyChange(
        name !== person.name ||
          email !== (person.email ?? '') ||
          phone !== (person.phone ?? '') ||
          klass !== person.operator_class,
      ),
    [
      name,
      email,
      phone,
      klass,
      person.name,
      person.email,
      person.phone,
      person.operator_class,
      onDirtyChange,
    ],
  );
  const save = useMutation({
    mutationFn: () =>
      api(t(`/people/${person.id}`), {
        method: 'PATCH',
        body: {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          operator_class: klass,
        },
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Field
        label="Nome e cognome"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        maxLength={120}
      />
      <Field
        label="Email"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        maxLength={254}
      />
      <Field
        label="Telefono"
        type="tel"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        maxLength={40}
      />
      <Select
        label="Come riceve il lavoro"
        value={klass}
        onChange={(event) => setKlass(event.target.value as OperatorClass)}
      >
        <option value="linea_operativa">Link personale ai compiti</option>
        <option value="knowledge_worker">Account e assistente AI</option>
      </Select>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <Button data-person-save type="submit" disabled={save.isPending || !name.trim()}>
        Salva dati
      </Button>
    </form>
  );
}

/** L'account personale è distinto dall'anagrafica e dai ruoli che la persona ricopre. */
function PersonAccountEditor({
  person,
  onDone,
  onChanged,
  onDirtyChange,
  onComplete,
}: { person: Person; onDone: () => void; onChanged: () => void; onComplete: () => void } & Dirty) {
  const { t } = useTenant();
  const [email, setEmail] = useState(person.email ?? '');
  const [invite, setInvite] = useState<{ url: string; expires_at: string; email: string } | null>(
    null,
  );
  const [copyMessage, setCopyMessage] = useState('');
  const [line, setLine] = useState<{ url: string; whatsapp_url: string | null } | null>(null);
  const share = useMutation({
    mutationFn: () =>
      api<{ url: string; whatsapp_url: string | null }>(t(`/people/${person.id}/line-link`), {
        method: 'POST',
      }),
    onSuccess: setLine,
  });
  useEffect(
    () => onDirtyChange(!person.user_id && !invite && email !== (person.email ?? '')),
    [email, invite, person.user_id, person.email, onDirtyChange],
  );
  const link = useMutation({
    mutationFn: () =>
      api<Person>(t(`/people/${person.id}/account`), { body: { email: email.trim() } }),
    onSuccess: onDone,
  });
  const invitation = useMutation({
    mutationFn: () =>
      api<{ url: string; expires_at: string; email: string }>(
        t(`/people/${person.id}/invitation`),
        { body: { email: email.trim() } },
      ),
    onSuccess: (result) => {
      setInvite(result);
      setCopyMessage('');
      onChanged();
    },
  });
  const unlink = useMutation({
    mutationFn: () => api<Person>(t(`/people/${person.id}/account`), { method: 'DELETE' }),
    onSuccess: onDone,
  });
  const finish = useMutation({
    mutationFn: () =>
      api<Person>(t(`/people/${person.id}`), {
        method: 'PATCH',
        body: { email: email.trim() || null },
      }),
    onSuccess: () => {
      onChanged();
      onComplete();
    },
  });
  const busy = finish.isPending || link.isPending || invitation.isPending || unlink.isPending;
  const error = finish.error ?? link.error ?? invitation.error ?? unlink.error;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite!.url);
      setCopyMessage('Link copiato.');
    } catch {
      setCopyMessage('Seleziona il link qui sopra e copialo.');
    }
  };
  return (
    <form
      className="w-full space-y-3 rounded-lg bg-black/[0.03] p-3 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy) return;
        if (!person.user_id && email.trim() !== (person.email ?? '')) finish.mutate();
        else onComplete();
      }}
    >
      <p className="text-ink/65">
        La persona è già stata creata. Puoi salvare l’email e terminare senza creare subito un
        invito.
      </p>
      <h3 className="font-semibold">Come accede {person.name}</h3>
      {person.user_id ? (
        <>
          <p className="text-ink/70">
            Account collegato. La persona entra con le proprie credenziali e trova i suoi compiti.
          </p>
          <p className="text-xs text-ink/55">
            Scollegando l’account si revocano gli inviti aperti e l’accesso come collaboratore. Gli
            accessi da titolare o amministratore restano.
          </p>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => unlink.mutate()}>
            Scollega account
          </Button>
        </>
      ) : (
        <>
          <p className="text-ink/70">
            Crea un invito personale. La persona potrà registrarsi o accedere con questa email e
            troverà i propri compiti.
          </p>
          <Field
            label="Email del suo account"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInvite(null);
            }}
            maxLength={254}
          />
          <Button
            type="button"
            disabled={busy || !email.trim().includes('@')}
            onClick={() => invitation.mutate()}
          >
            {invitation.isPending ? 'Creo l’invito…' : 'Crea link d’invito'}
          </Button>
          <details className="rounded-lg border border-black/10 p-3">
            <summary className="cursor-pointer text-sm text-ink/65">
              La persona ha già un account nell’azienda
            </summary>
            <p className="my-2 text-xs text-ink/60">
              Collega direttamente l’account già abilitato, usando la stessa email.
            </p>
            <Button
              type="button"
              variant="ghost"
              disabled={busy || !email.trim().includes('@')}
              onClick={() => link.mutate()}
            >
              Collega account esistente
            </Button>
          </details>
          {invite && (
            <div className="space-y-2 rounded-lg bg-white p-3">
              <p>
                Invito per <strong>{invite.email}</strong>. Scade il{' '}
                {new Date(invite.expires_at).toLocaleString('it-IT')}.
              </p>
              <p className="text-xs text-ink/60">
                Condividi il link con la persona. Per accettarlo dovrà accedere o registrarsi con
                questa email. Un nuovo invito sostituisce il precedente.
              </p>
              <input
                aria-label="Link d’invito"
                className="w-full rounded-lg border border-black/15 px-3 py-2 text-xs"
                value={invite.url}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <Button type="button" variant="ghost" onClick={() => void copy()}>
                Copia link
              </Button>
              {copyMessage && (
                <p role="status" className="text-xs text-ink/60">
                  {copyMessage}
                </p>
              )}
            </div>
          )}
        </>
      )}
      <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
      {!person.user_id && (
        <details className="rounded-lg border border-black/10 p-3">
          <summary className="cursor-pointer text-sm text-ink/65">
            Accesso senza account con link compiti
          </summary>
          <p className="my-2 text-xs text-ink/60">
            Il link personale apre soltanto i compiti della persona. Creandone uno nuovo, il
            precedente smette di funzionare.
          </p>
          <Button
            type="button"
            variant="ghost"
            disabled={share.isPending}
            onClick={() => share.mutate()}
          >
            Crea link compiti
          </Button>
          {line && (
            <div className="mt-3 space-y-2">
              <Field
                label="Link personale ai compiti"
                value={line.url}
                readOnly
                onFocus={(event) => event.target.select()}
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => void navigator.clipboard?.writeText(line.url)}
              >
                Copia link compiti
              </Button>
              {line.whatsapp_url && (
                <a
                  href={line.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-3 text-accent underline"
                >
                  Apri WhatsApp
                </a>
              )}
            </div>
          )}
          <ErrorText>{share.error ? errorMessage(share.error) : ''}</ErrorText>
        </details>
      )}
      <div className="sticky bottom-0 -mx-3 -mb-3 rounded-b-lg border-t border-black/10 bg-white p-3">
        <Button data-person-save type="submit" disabled={busy}>
          {finish.isPending ? 'Salvo…' : 'Salva e termina'}
        </Button>
        <p className="mt-2 text-xs text-ink/60">
          Salva l’email nella scheda e chiude la configurazione. Nessun invito viene inviato.
        </p>
      </div>
    </form>
  );
}

/**
 * Responsabile delle procedure: le aree della pagina Procedure che la persona può verificare, cambiare e completare
 * con il suo account già abilitato nell'azienda.
 */
function ProcedureAreasEditor({
  person,
  onDone,
  onCancel,
  onDirtyChange,
}: { person: Person; onDone: () => void; onCancel: () => void } & Dirty) {
  const { t } = useTenant();
  const state = useTenantQuery<OnboardingState>(['onboarding'], '/onboarding');
  const [chosen, setChosen] = useState<string[]>(person.procedure_areas);
  const [email, setEmail] = useState(person.email ?? '');
  useEffect(
    () =>
      onDirtyChange(
        JSON.stringify([...chosen].sort()) !== JSON.stringify([...person.procedure_areas].sort()) ||
          email !== (person.email ?? ''),
      ),
    [chosen, email, person.procedure_areas, person.email, onDirtyChange],
  );
  const save = useMutation({
    mutationFn: () =>
      api<Person>(t(`/people/${person.id}`), {
        method: 'PATCH',
        body: {
          procedure_areas: chosen,
          ...(!person.user_id && email.trim() && email.trim() !== person.email
            ? { email: email.trim() }
            : {}),
        },
      }),
    onSuccess: onDone,
  });
  const areas = state.data?.areas.map((a) => a.area) ?? [];
  const toggle = (a: string) =>
    setChosen(chosen.includes(a) ? chosen.filter((x) => x !== a) : [...chosen, a]);
  return (
    <div className="w-full rounded-lg bg-black/[0.03] p-3 text-sm">
      <p className="mb-2 text-ink/70">
        Aree di cui {person.name} è responsabile: con il suo account le vede nella pagina Procedure,
        le verifica, le cambia e ne aggiunge, solo lì.
      </p>
      {state.isLoading && <p className="text-xs text-ink/50">Carico le aree…</p>}
      {state.data && !areas.length && (
        <p className="text-xs text-ink/50">
          Le aree compaiono quando la mappa delle procedure è pronta.
        </p>
      )}
      <div className="mb-3 flex flex-wrap gap-2">
        {areas.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => toggle(a)}
            className={`rounded-full border px-3 py-1 text-xs ${chosen.includes(a) ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}
          >
            {a}
          </button>
        ))}
      </div>
      {!person.user_id && chosen.length > 0 && (
        <Field
          label="Email del suo account già abilitato nell’azienda"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      )}
      <div className="mt-2 flex gap-2">
        <Button
          data-person-save
          disabled={
            save.isPending || (!person.user_id && chosen.length > 0 && !email.includes('@'))
          }
          onClick={() => save.mutate()}
        >
          Salva
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}

function PersonSitesEditor({
  person,
  roles,
  onDone,
  onDirtyChange,
}: { person: Person; roles: Role[]; onDone: () => void } & Dirty) {
  const { t } = useTenant();
  const { sites } = useSites();
  const [scope, setScope] = useState<'all' | 'some' | 'company'>(
    person.all_sites ? 'all' : person.sites.length ? 'some' : 'company',
  );
  const [chosen, setChosen] = useState<Record<string, string[]>>(
    Object.fromEntries(person.sites.map((site) => [site.site_id, site.roles])),
  );
  const stable = (value: Record<string, string[]>) =>
    JSON.stringify(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, roles]) => [key, [...roles].sort()]),
    );
  const original = stable(
    Object.fromEntries(person.sites.map((site) => [site.site_id, site.roles])),
  );
  const initialScope = person.all_sites ? 'all' : person.sites.length ? 'some' : 'company';
  useEffect(
    () =>
      onDirtyChange(scope !== initialScope || (scope === 'some' && stable(chosen) !== original)),
    [scope, chosen, original, initialScope, onDirtyChange],
  );
  const save = useMutation({
    mutationFn: () =>
      api(t(`/people/${person.id}/sites`), {
        method: 'PUT',
        body: {
          tutte: scope === 'all',
          sedi:
            scope === 'some'
              ? Object.entries(chosen).map(([site_id, roles]) => ({ site_id, roles }))
              : [],
        },
      }),
    onSuccess: onDone,
  });
  const toggleSite = (id: string) =>
    setChosen((previous) => {
      const next = { ...previous };
      if (id in next) delete next[id];
      else next[id] = [];
      return next;
    });
  const toggleRole = (id: string, role: string) =>
    setChosen((previous) => ({
      ...previous,
      [id]: previous[id]!.includes(role)
        ? previous[id]!.filter((key) => key !== role)
        : [...previous[id]!, role],
    }));
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <fieldset className="space-y-3">
        <legend className="mb-2 font-semibold">Dove svolge i suoi ruoli?</legend>
        <p className="text-sm text-ink/60">
          Le attività dell’azienda restano disponibili secondo i permessi del ruolo. Qui scegli
          anche le sedi in cui opera.
        </p>
        {(
          [
            ['all', 'Azienda e tutte le sedi'],
            ['some', 'Azienda e alcune sedi'],
            ['company', 'Solo attività dell’azienda'],
          ] as const
        ).map(([key, label]) => (
          <label
            key={key}
            className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${scope === key ? 'border-accent/30 bg-accent/[0.04]' : 'border-black/10'}`}
          >
            <input
              type="radio"
              name={`scope-${person.id}`}
              value={key}
              checked={scope === key}
              onChange={() => setScope(key)}
              className="mt-0.5"
            />
            {label}
          </label>
        ))}
      </fieldset>
      {scope === 'some' && (
        <div className="space-y-3">
          {sites.map((site) => (
            <div key={site.id} className="rounded-lg border border-black/10 p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={site.id in chosen}
                  onChange={() => toggleSite(site.id)}
                />
                {site.name}
              </label>
              {site.id in chosen && (
                <div className="ml-6 mt-2 space-y-2">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!chosen[site.id]!.length}
                      onChange={(event) =>
                        setChosen((previous) => ({
                          ...previous,
                          [site.id]: event.target.checked ? [] : [...person.roles],
                        }))
                      }
                      className="mt-0.5"
                    />
                    Tutti i suoi ruoli previsti in questa sede
                  </label>
                  {chosen[site.id]!.length > 0 && (
                    <>
                      <p className="text-xs text-ink/60">Seleziona almeno un ruolo:</p>
                      {person.roles.map((role) => (
                        <label key={role} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={chosen[site.id]!.includes(role)}
                            disabled={
                              chosen[site.id]!.length === 1 && chosen[site.id]!.includes(role)
                            }
                            onChange={() => toggleRole(site.id, role)}
                            className="mt-0.5"
                          />
                          {roles.find((item) => item.key === role)?.label ?? humanize(role)}
                        </label>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {scope === 'company' && (
        <p className="text-sm text-ink/60">Non riceverà compiti riferiti a una sede specifica.</p>
      )}
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <Button
        data-person-save
        type="submit"
        disabled={save.isPending || (scope === 'some' && !Object.keys(chosen).length)}
      >
        Salva sedi e continua
      </Button>
    </form>
  );
}
